import { Typography, useTheme } from "@mui/material";
import { Small } from "components/Typography";
import { showCurrent, selectStatusCurrent } from "features/auth/authSlice";
import { useEffect, useState, useCallback, useRef } from "react";
import { DialogModal } from "./modals/DialogModal";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useLazyValidateSessionQuery } from "features/security/auth";
import { textAuth } from "config/configMessages";
import { sessionTimeLabelFlag, sessionTimeClickFlag, sessionTimeTitleFlag, downtimeTagFlag, downtimeTitleFlag, inactivityConfiguration, sessionWarningConfiguration, toastDuration } from "config/constants";
import toast from 'react-hot-toast';
import _ from 'lodash';

const CountdownTimer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [openCurrentModal, setOpenCurrentModal] = useState(false)
  const [messageModal, setMessageModal] = useState("");

  // Nuevos estados para control de inactividad
  const [openInactivityModal, setOpenInactivityModal] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [inactivityElapsed, setInactivityElapsed] = useState(0);

  // Estados para el modal de advertencia de sesión
  const [openSessionWarningModal, setOpenSessionWarningModal] = useState(false);
  const [hasShownWarning, setHasShownWarning] = useState(false);

  const inactivityTimerRef = useRef(null);
  const modalTimerRef = useRef(null);
  const openInactivityModalRef = useRef(false);

  const sessionTime = parseInt(localStorage.getItem("timeSesion"), 10) * 60 || 3600;
  const statusCurrent = useSelector(selectStatusCurrent);
  // const [validateSession, { data, isError, isSuccess }] = useLazyValidateSessionQuery();
  // const [hasValidatedSession, setHasValidatedSession] = useState(false);
  const [timeLeft, setTimeLeft] = useState(() => {
    const savedEndTime = localStorage.getItem("endTime");
    if (savedEndTime) {
      const timeDifference = Math.floor((new Date(savedEndTime) - new Date()) / 1000);
      return timeDifference > 0 ? timeDifference : 0;
    }
    return sessionTime;
  });

  // useEffect(() => {
  //   if (isError) {
  //     setHasValidatedSession(false)
  //     setMessageModal(textAuth.concurrentSession)
  //     setOpenCurrentModal(true)
  //   } else if (isSuccess && hasValidatedSession) {
  //     setHasValidatedSession(false)
  //     setOpen(true);
  //   }
  // }, [data, isError, isSuccess]);

  useEffect(() => {
    if (statusCurrent === "failed") {
      setMessageModal(textAuth.errorRefreshToken)
      setOpenCurrentModal(true)
    }
  }, [statusCurrent]);


  //  Efecto para mantener sincronizado el ref con el estado del modal de inactividad
  // Necesario para evitar stale closures en los event listeners

  useEffect(() => {
    openInactivityModalRef.current = openInactivityModal;
  }, [openInactivityModal]);


  // Efecto principal para el manejo del tiempo de sesión
  // - Inicializa endTime en localStorage si no existe
  // - Actualiza el contador de tiempo restante cada segundo
  // - Muestra advertencia cuando quedan X minutos (configurable)
  // - Redirige a logout cuando el tiempo se agota
  useEffect(() => {
    if (!localStorage.getItem("endTime")) {
      const endTime = new Date();
      endTime.setSeconds(endTime.getSeconds() + sessionTime);
      localStorage.setItem("endTime", endTime);
    }

    const timer = setInterval(() => {
      setTimeLeft(() => {
        const savedEndTime = localStorage.getItem("endTime");
        const timeDifference = Math.floor((new Date(savedEndTime) - new Date()) / 1000);

        // Mostrar advertencia para alargar el token cuando quedan X minutos (configurable)
        if (timeDifference <= sessionWarningConfiguration.warningTime &&
          timeDifference > 0 &&
          !hasShownWarning &&
          !openSessionWarningModal) {
          setOpenSessionWarningModal(true);
          setHasShownWarning(true);
        }

        if (timeDifference <= 0) {
          clearInterval(timer);  // Limpia el intervalo
          navigate("/logout");  // Navega a la página de logout
        }

        return timeDifference > 0 ? timeDifference : 0;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [sessionTime, open, navigate, hasShownWarning, openSessionWarningModal]);


  // Función para resetear el timer de inactividad cuando se detecta actividad del usuario
  // - Actualiza el timestamp de última actividad
  // - Resetea el contador visual a 00:00
  // - Limpia el timer existente
  // - Crea un nuevo timer si no hay modal abierto   
  const resetInactivityTimer = useCallback(() => {
    setLastActivity(Date.now());
    setInactivityElapsed(0); // Resetear contador visual inmediatamente

    // Limpiar timer existente
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Solo resetear si no hay modal de inactividad abierto
    if (!openInactivityModalRef.current) {
      const newTimer = setTimeout(() => {
        showInactivityModal();
      }, inactivityConfiguration.inactivityTimeout * 1000);

      inactivityTimerRef.current = newTimer;
    }
  }, []);

  //configurabler el tiempo de x segundos 
  const throttledResetInactivityTimer = useCallback(
    _.throttle(
      resetInactivityTimer, inactivityConfiguration.mouseMoveCaptureTime * 1000),
    [resetInactivityTimer]
  );

  // Función para mostrar el modal de inactividad
  //  - Abre el modal "¿Sigues ahí?"
  //  - Configura timer automático para cerrar sesión si no hay respuesta
  const showInactivityModal = useCallback(() => {
    setOpenInactivityModal(true);

    const autoCloseTimer = setTimeout(() => {
      setOpenInactivityModal(false);
      navigate("/logout");
    }, inactivityConfiguration.inactivityModalTimeout * 1000);

    modalTimerRef.current = autoCloseTimer;
  }, [navigate]);


  //  Función para manejar la respuesta del usuario al modal de inactividad
  //   - Cierra ambos modales (inactividad y advertencia de sesión)
  //   - Limpia los timers
  //   - Extiende el tiempo de sesión completo
  //   - Refresca el token de autenticación
  //   - Reinicia el timer de inactividad  
  const handleInactivityResponse = useCallback(() => {
    openInactivityModalRef.current = false;
    setOpenInactivityModal(false);

    // También cerrar modal de advertencia de sesión si está abierto
    setOpenSessionWarningModal(false);

    if (modalTimerRef.current) {
      clearTimeout(modalTimerRef.current);
    }

    // Renovar la sesión completa
    const endTime = new Date();
    endTime.setSeconds(endTime.getSeconds() + sessionTime);
    localStorage.setItem("endTime", endTime);
    setTimeLeft(sessionTime);

    // Resetear flag para permitir nueva advertencia en el futuro
    setHasShownWarning(false);

    dispatch(showCurrent());
    resetInactivityTimer();
  }, [dispatch, resetInactivityTimer, sessionTime]);

  // Funcion para manejar la respuesta afirmativa del modal de advertencia de sesión
  // Extiende el tiempo de sesión completo
  // Muestra toast de confirmación
  // Resetea el flag de advertencia para permitir futuras advertencias
  // Refresca el token de autenticación
  // Cierra el modal de inactividad si está abierto
  const handleSessionWarningExtend = useCallback(() => {
    // Cerrar modal de inactividad si está abierto
    setOpenInactivityModal(false);
    setOpenSessionWarningModal(false);


    // Limpiar timer del modal de inactividad si existe
    if (modalTimerRef.current) {
      clearTimeout(modalTimerRef.current);
    }

    // Renovar la sesión completa
    const endTime = new Date();
    endTime.setSeconds(endTime.getSeconds() + sessionTime);
    localStorage.setItem("endTime", endTime);
    setTimeLeft(sessionTime);

    // Resetear flag para permitir nueva advertencia en el futuro
    setHasShownWarning(false);

    // Refrescar token
    dispatch(showCurrent());

    // Resetear timer de inactividad
    resetInactivityTimer();

    // toast de confirmacion
    toast(
      <div style={{ textAlign: 'center' }}>
        <div style={{
          fontWeight: 'bold',
          fontSize: '14px',
          marginBottom: '4px',
          color: theme.palette.text.primary
        }}>
          {textAuth.toastSessionExtended}
        </div>
        <div style={{
          fontSize: '13px',
          color: theme.palette.text.secondary
        }}>
          {textAuth.toastSessionExtendedMessage}
        </div>
      </div>,
      {
        duration: toastDuration,
        position: 'top-center',
        style: {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          padding: '12px 16px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          border: `1px solid ${theme.palette.divider}`
        },
        icon: false
      }
    );
  }, [dispatch, sessionTime, theme.palette, resetInactivityTimer]);


  //  Función para manejar el clic en el label de sesión(solo para desarrollo)
  //  Simula que se alcanzó el tiempo de advertencia de sesión
  const handleSessionLabelClick = useCallback(() => {
    if (!openSessionWarningModal) {
      setOpenSessionWarningModal(true);
    }
  }, [openSessionWarningModal]);

  //  Función para manejar la respuesta negativa del modal de advertencia de sesión
  //  Cierra el modal sin hacer cambios
  const handleSessionWarningCancel = useCallback(() => {
    setOpenSessionWarningModal(false);
  }, []);

  // Efecto para configurar los event listeners que detectan actividad del usuario
  // Eventos monitoreados:
  //  - click: clics en cualquier elemento de la interfaz
  //  - input: escritura en campos de formulario
  //  - keydown: teclas presionadas (solo en campos de formulario)
  //  - scroll: desplazamiento por la página
  //  - mousemove: movimiento del mouse (throttled)
  useEffect(() => {
    const events = ['click', 'input', 'keydown', 'scroll', 'mousemove'];

    const handleActivity = (event) => {
      // Si el modal de inactividad está abierto, ignorar toda actividad
      if (openInactivityModalRef.current) return;

      // Solo considerar actividad válida en elementos de formulario para keydown
      if (event.type === 'keydown') {
        const target = event.target;
        const isFormElement = target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.contentEditable === 'true';

        if (!isFormElement) {
          return;
        }
      }

      if (event.type === 'mousemove') {
        throttledResetInactivityTimer();
      } else {
        resetInactivityTimer();
      }
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    resetInactivityTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });

      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }

      if (modalTimerRef.current) {
        clearTimeout(modalTimerRef.current);
      }
    };
  }, [resetInactivityTimer]);

  // Efecto para detectar navegación entre pantallas y secciones del sistema
  // Resetea el timer de inactividad cuando cambia la ruta o parámetros
  useEffect(() => {
    resetInactivityTimer();
  }, [location.pathname, location.search, resetInactivityTimer]);

  // Efecto para actualizar el contador visual de inactividad en tiempo real
  // Se ejecuta cada segundo para mostrar cuánto tiempo ha transcurrido desde la última actividad
  useEffect(() => {
    const inactivityCounterTimer = setInterval(() => {
      if (!openInactivityModal) {
        const elapsed = Math.floor((Date.now() - lastActivity) / 1000);
        setInactivityElapsed(elapsed);
      }
    }, 1000);

    return () => clearInterval(inactivityCounterTimer);
  }, [lastActivity, openInactivityModal]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // const restartSession = () => {
  //   const endTime = new Date();
  //   endTime.setSeconds(endTime.getSeconds() + sessionTime);
  //   localStorage.setItem("endTime", endTime);
  //   setTimeLeft(sessionTime);
  //   setOpen(false);
  //   dispatch(showCurrent());  // Acción para reiniciar la sesión
  // };

  return (
    <>
      <DialogModal
        open={openCurrentModal}
        onClose={() => setOpenCurrentModal(false)}
        title={textAuth.titleSessionExpiration}
        textInfo={
          <Typography component="span" variant="body1">
            {messageModal}
            <br />
          </Typography>
        }
        onClickHandler={() => {
          setOpenCurrentModal(false)
          navigate("/logout");
        }}
        buttonName="Aceptar"
      />
      <DialogModal
        open={openInactivityModal}
        title={textAuth.titleInactivityDetected}
        textInfo={
          <Typography component="span" variant="body1">
            {textAuth.inactivityMessage}
            <br />
          </Typography>
        }
        onClickHandler={handleInactivityResponse}
        buttonName="Sí"
        maxWidth="sm"
        fullWidth={true}
      />

      <DialogModal
        open={openSessionWarningModal}
        title={textAuth.titleSessionWarning}
        textInfo={
          <Typography component="span" variant="body1">
            {textAuth.sessionWarningMessage}
            <br />
          </Typography>
        }
        showCancel={true}
        onClickHandler={handleSessionWarningExtend}
        onClose={handleSessionWarningCancel}
        buttonName="Sí"
        buttonNameSecondary="Cancelar"
        maxWidth="sm"
        fullWidth={true}
      />

      {sessionTimeLabelFlag && (
        <>
          <Small
            fontWeight="600"
            display="inline"
            onClick={sessionTimeClickFlag ? handleSessionLabelClick : undefined}
            sx={{
              marginRight: 2,
              ...(sessionTimeClickFlag && {
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': {
                  color: theme.palette.primary.main,
                  transition: 'color 0.2s ease-in-out'
                }
              })
            }}
          >
            {sessionTimeTitleFlag && textAuth.sessionTitle} {formatTime(timeLeft)}
          </Small>
        </>
        // Label Antes de cambios - Solo mostraba el tiempo restante sin posibilidad de clic
        // <Small fontWeight="600" display="inline">
        //   {formatTime(timeLeft)}
        // </Small>
      )}
      {downtimeTagFlag && (
        <>
          <Small fontWeight="600" display="inline" sx={{ marginRight: 2 }}>
            {downtimeTitleFlag && textAuth.inactivityTitle} {formatTime(inactivityElapsed)}
          </Small>
        </>
      )}
    </>
  );
};

export default CountdownTimer;